import { Button, useThemeMode } from "@meiden-connect/ui";
import { useQuery } from "@tanstack/react-query";
import {
  useCanvasApi,
  type AllCanvasItem,
  type FavoriteCanvasItem,
  type RecentCanvasItem,
} from "../../lib/canvas-api";
import { generateCanvasId } from "../../lib/canvas-id";
import { normalizeCanvasTitle } from "../../lib/canvas-title";
import { formatJstDateTime } from "../../lib/datetime";
import { navigate } from "../../router";
import { RecentCanvasCard } from "./recent-canvas-card";

// ダッシュボード（TOP）画面。
// TOP のみライト/ダーク両対応とし、ヒーロー画像もテーマに合わせて切り替える。
export function DashboardView(): JSX.Element {
  const canvasApi = useCanvasApi();
  const themeMode = useThemeMode();
  const heroImageSrc = themeMode === "dark" ? "/ogp-dark.png" : "/ogp.png";

  const handleCreate = (): void => {
    const canvasId = generateCanvasId();
    navigate(`/c/${canvasId}`);
  };

  const recentCanvasesQuery = useQuery({
    queryKey: ["recent-canvases"],
    queryFn: canvasApi.fetchRecentCanvases,
    // 履歴の更新は他タブの操作でも起きるため、TOP に戻ったタイミングで再取得する。
    refetchOnMount: true,
  });
  const recentCanvases: readonly RecentCanvasItem[] = recentCanvasesQuery.data?.items ?? [];

  // お気に入り（個人単位）。1 件以上ある場合のみセクションを表示する。
  const favoritesQuery = useQuery({
    queryKey: ["favorites"],
    queryFn: canvasApi.fetchFavorites,
    refetchOnMount: true,
  });
  const favorites: readonly FavoriteCanvasItem[] = favoritesQuery.data?.items ?? [];

  // 管理者判定。canvas-admin 権限のあるユーザーにのみ「全キャンバス一覧」を出す。
  const meQuery = useQuery({ queryKey: ["me"], queryFn: canvasApi.fetchMe });
  const canAdmin = meQuery.data?.actor.permissions.canAdmin ?? false;

  // 全キャンバス一覧（管理者専用）。canAdmin が確定してから取得する。
  const allCanvasesQuery = useQuery({
    queryKey: ["all-canvases"],
    queryFn: canvasApi.fetchAllCanvases,
    enabled: canAdmin,
    refetchOnMount: true,
  });
  const allCanvases: readonly AllCanvasItem[] = allCanvasesQuery.data?.items ?? [];

  return (
    <div className="dashboard-content" data-testid="dashboard-root">
      <section className="dashboard-hero-card">
        <div className="dashboard-hero-copy">
          <p className="dashboard-eyebrow">MEIDEN CONNECT Collaborative Canvas</p>
          <h1>リファインメントキャンバスへようこそ</h1>
          <p>
            プロダクトバックログ・リファインメントを共同編集で進めるためのお絵かきツールです。
            新規キャンバスを作成するか、最近使ったキャンバスを開いてください。
          </p>
          <div className="dashboard-hero-actions">
            <Button type="button" variant="action emphasis" onClick={handleCreate}>
              + 新規キャンバスを作成
            </Button>
            <Button type="button" variant="action" onClick={() => navigate("/mcp-setup")}>
              AI（MCP）から操作する
            </Button>
          </div>
        </div>
        <picture className="dashboard-hero-image-frame">
          <img alt="リファインメントキャンバスの紹介画像" src={heroImageSrc} />
        </picture>
      </section>

      <section className="dashboard-card">
        <h2>最近使ったキャンバス</h2>
        {recentCanvasesQuery.isLoading ? (
          <p className="dashboard-muted">読み込み中…</p>
        ) : recentCanvasesQuery.isError ? (
          <p className="dashboard-danger">履歴の取得に失敗しました。時間をおいて再読み込みしてください。</p>
        ) : recentCanvases.length === 0 ? (
          <p className="dashboard-muted">まだ最近使ったキャンバスはありません。</p>
        ) : (
          <div className="recent-canvas-grid">
            {recentCanvases.map((canvas) => (
              <RecentCanvasCard
                key={canvas.canvasId}
                canvasId={canvas.canvasId}
                title={normalizeCanvasTitle(canvas.title)}
                updatedAt={formatJstDateTime(canvas.lastOpenedAt)}
                thumbnailUrl={canvas.thumbnailUrl}
              />
            ))}
          </div>
        )}
      </section>

      {/* お気に入りは 1 件以上あるときだけ表示する（個人単位）。 */}
      {favorites.length > 0 ? (
        <section className="dashboard-card" data-testid="dashboard-favorites">
          <h2>お気に入り</h2>
          <div className="recent-canvas-grid">
            {favorites.map((canvas) => (
              <RecentCanvasCard
                key={canvas.canvasId}
                canvasId={canvas.canvasId}
                title={normalizeCanvasTitle(canvas.title)}
                updatedAt={formatJstDateTime(canvas.updatedAt)}
                thumbnailUrl={canvas.thumbnailUrl}
              />
            ))}
          </div>
        </section>
      ) : null}

      {/* 全キャンバス一覧は管理者にのみ表示する（更新日時の新しい順・最大 48 件）。 */}
      {canAdmin ? (
        <section className="dashboard-card" data-testid="dashboard-all-canvases">
          <h2>全キャンバス一覧</h2>
          {allCanvasesQuery.isLoading ? (
            <p className="dashboard-muted">読み込み中…</p>
          ) : allCanvasesQuery.isError ? (
            <p className="dashboard-danger">
              全キャンバス一覧の取得に失敗しました。時間をおいて再読み込みしてください。
            </p>
          ) : allCanvases.length === 0 ? (
            <p className="dashboard-muted">キャンバスはまだありません。</p>
          ) : (
            <div className="recent-canvas-grid">
              {allCanvases.map((canvas) => (
                <RecentCanvasCard
                  key={canvas.canvasId}
                  canvasId={canvas.canvasId}
                  title={normalizeCanvasTitle(canvas.title)}
                  updatedAt={formatJstDateTime(canvas.updatedAt)}
                  thumbnailUrl={canvas.thumbnailUrl}
                />
              ))}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
